import { Empty, Spin, Table } from 'antd';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import get from 'lodash/get';
import UserShortCard from '@components/UserShortCard';
import useTranslate from '@hooks/useTranslate';
import componentContent from './TribeClaimsList.content.json';
import { getClaimsByTribe } from '@stores/tribes';
import { goTo } from '@helpers/navigation';
import { FULL_TIME_FORMAT } from '@config/format';

type TribeClaim = {
  id: string;
  type?: string;
  text?: string;
  created?: string;
  complainer?: { id: string; [k: string]: any };
};

interface TribeClaimsListProps {
  tribeId: string;
}

const TribeClaimsList: FunctionComponent<TribeClaimsListProps> = ({ tribeId }) => {
  const content = useTranslate(componentContent);

  const [isLoading, setIsLoading] = useState(false);
  const [claims, setClaims] = useState<TribeClaim[]>([]);

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      setIsLoading(true);
      try {
        const res = await getClaimsByTribe(tribeId);
        const list = get(res, ['data', 'body', 'result', 'page'], []);
        if (isMounted) setClaims(Array.isArray(list) ? list : []);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [tribeId]);

  const columns = useMemo(
    () => [
      {
        title: content.complainer,
        dataIndex: 'complainer',
        key: 'complainer',
        render: (u: any) =>
          u ? (
            <UserShortCard
              user={u}
              onClick={() => goTo('userDetail', { userId: u?.id })}
            />
          ) : (
            '—'
          ),
      },
      {
        title: content.reason,
        dataIndex: 'type',
        key: 'type',
        render: (v: string) => v || '—',
      },
      {
        title: content.comment,
        dataIndex: 'text',
        key: 'text',
        render: (v: string) => v || '—',
      },
      {
        title: content.date,
        dataIndex: 'created',
        key: 'created',
        render: (v: string) => (v ? moment(v).format(FULL_TIME_FORMAT) : '—'),
      },
    ],
    [content],
  );

  return (
    <Spin spinning={isLoading}>
      {claims.length ? (
        <Table
          size='small'
          dataSource={claims}
          columns={columns as any}
          rowKey={(r: any) => r.id}
          pagination={false}
        />
      ) : (
        <Empty />
      )}
    </Spin>
  );
};

export default TribeClaimsList;
